import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API_URL, formatApiError } from '../../utils/api';
import { Switch } from '../ui/switch';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import {
  Clock, Calendar, CheckCircle, XCircle, WarningCircle, MinusCircle,
} from '@phosphor-icons/react';
import { toast } from 'sonner';

const authHeaders = () => {
  const token = localStorage.getItem('access_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const pad2 = (n) => String(Math.max(0, Math.min(59, Number(n) || 0))).padStart(2, '0');

const STATUS_META = {
  success: { icon: CheckCircle, tone: 'text-[#10B981]', label: 'Success' },
  partial: { icon: WarningCircle, tone: 'text-[#F59E0B]', label: 'Partial' },
  failed: { icon: XCircle, tone: 'text-[#EF4444]', label: 'Failed' },
  no_config: { icon: MinusCircle, tone: 'text-[#78716C]', label: 'No config' },
  skipped: { icon: MinusCircle, tone: 'text-[#78716C]', label: 'Skipped' },
};

const relativeAgo = (iso) => {
  if (!iso) return '—';
  try {
    const then = new Date(iso).getTime();
    const diffMs = Date.now() - then;
    const mins = Math.floor(diffMs / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    return `${days}d ago`;
  } catch {
    return '—';
  }
};

export const ScheduledJobsConfig = () => {
  const [catalog, setCatalog] = useState([]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingKey, setSavingKey] = useState('');

  const fetchMatrix = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/scheduled-jobs`, { withCredentials: true, headers: authHeaders() });
      setCatalog(data?.catalog || []);
      setRows(data?.countries || []);
    } catch (e) {
      toast.error(formatApiError(e, 'Failed to load scheduled jobs'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchMatrix(); }, [fetchMatrix]);

  const persist = async (jobKey, cc, patch) => {
    const key = `${jobKey}:${cc}:${Object.keys(patch)[0]}`;
    setSavingKey(key);
    try {
      const { data } = await axios.put(
        `${API_URL}/api/scheduled-jobs/${jobKey}/${cc}`,
        patch,
        { withCredentials: true, headers: authHeaders() },
      );
      const updated = data?.job || {};
      setRows(rs => rs.map(r => (r.country_code === cc
        ? { ...r, jobs: { ...r.jobs, [jobKey]: {
            ...r.jobs[jobKey],
            send_hour: updated.send_hour ?? r.jobs[jobKey].send_hour,
            send_minute: updated.send_minute ?? r.jobs[jobKey].send_minute,
            enabled: updated.enabled !== undefined ? updated.enabled : r.jobs[jobKey].enabled,
          } } }
        : r)));
      toast.success('Saved');
    } catch (e) {
      toast.error(formatApiError(e, 'Failed to save'));
    } finally {
      setSavingKey('');
    }
  };

  const onTimeChange = (jobKey, cc, hhmm) => {
    const [hh, mm] = (hhmm || '06:30').split(':');
    persist(jobKey, cc, { send_hour: Number(hh), send_minute: Number(mm) });
  };

  return (
    <div className="bg-[#FFFFFF] border border-[#EDE6D8] rounded-lg p-6 mt-6" data-testid="scheduled-jobs-config">
      <div className="flex items-center gap-2 mb-1">
        <Clock size={20} className="text-[#0A2540]" />
        <h2 className="text-lg font-semibold text-[#0A2540] tracking-tight" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
          Scheduled Jobs
        </h2>
      </div>
      <p className="text-sm text-[#78716C] mb-5" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
        Choose the local time each automated job runs in every country. Individual entity opt-ins and recipients are managed under Configurations → Emails.
      </p>

      {loading ? (
        <div className="py-8 text-center text-sm text-[#78716C]" data-testid="scheduled-jobs-loading">Loading…</div>
      ) : catalog.length === 0 ? (
        <div className="py-8 text-center text-sm text-[#78716C]">No jobs available.</div>
      ) : (
        <div className="space-y-6">
          {catalog.map(job => (
            <div key={job.key} className="border border-[#EDE6D8] rounded-lg overflow-hidden" data-testid={`job-block-${job.key}`}>
              <div className="bg-[#FCFAF6] px-4 py-3 border-b border-[#EDE6D8]">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold text-[#0A2540]" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>{job.label}</span>
                  <Badge className="bg-[#E7F5EC] text-[#166534] hover:bg-[#E7F5EC] border-0 text-[10px]">{job.key}</Badge>
                </div>
                <p className="text-xs text-[#78716C] mt-1">{job.description}</p>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-white text-left border-b border-[#EDE6D8]">
                      <th className="px-4 py-2.5 text-[11px] uppercase tracking-wider text-[#78716C] font-semibold" style={{ fontFamily: 'JetBrains Mono, monospace' }}>Country</th>
                      <th className="px-4 py-2.5 text-[11px] uppercase tracking-wider text-[#78716C] font-semibold w-40" style={{ fontFamily: 'JetBrains Mono, monospace' }}>Timezone</th>
                      <th className="px-4 py-2.5 text-[11px] uppercase tracking-wider text-[#78716C] font-semibold w-28" style={{ fontFamily: 'JetBrains Mono, monospace' }}>Time (Local)</th>
                      <th className="px-4 py-2.5 text-[11px] uppercase tracking-wider text-[#78716C] font-semibold w-24" style={{ fontFamily: 'JetBrains Mono, monospace' }}>Enabled</th>
                      <th className="px-4 py-2.5 text-[11px] uppercase tracking-wider text-[#78716C] font-semibold w-48" style={{ fontFamily: 'JetBrains Mono, monospace' }}>Last Run</th>
                      <th className="px-4 py-2.5 text-[11px] uppercase tracking-wider text-[#78716C] font-semibold w-40" style={{ fontFamily: 'JetBrains Mono, monospace' }}>Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#EDE6D8]">
                    {rows.length === 0 && (
                      <tr><td colSpan={6} className="px-4 py-6 text-center text-xs text-[#78716C]">No countries with provisioned entities yet.</td></tr>
                    )}
                    {rows.map(row => {
                      const j = row.jobs?.[job.key] || {};
                      const timeStr = `${pad2(j.send_hour)}:${pad2(j.send_minute)}`;
                      const meta = j.last_run_status ? STATUS_META[j.last_run_status] : null;
                      const StatusIcon = meta?.icon;
                      return (
                        <tr key={row.country_code} className="hover:bg-[#FCFAF6]/50" data-testid={`job-row-${job.key}-${row.country_code}`}>
                          <td className="px-4 py-3">
                            <div className="font-medium text-[#1C1917]" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                              {row.country_name || row.country_code}
                            </div>
                            <div className="text-[10px] uppercase tracking-wider text-[#78716C]" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                              {row.country_code}
                            </div>
                          </td>
                          <td className="px-4 py-3 text-xs text-[#78716C]" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                            {row.time_zone || '—'}
                          </td>
                          <td className="px-4 py-3">
                            <Input
                              type="time"
                              value={timeStr}
                              disabled={savingKey.startsWith(`${job.key}:${row.country_code}:send_`)}
                              onChange={(e) => onTimeChange(job.key, row.country_code, e.target.value)}
                              className="w-28 border-[#EDE6D8] rounded-md h-8 text-sm"
                              data-testid={`job-time-${job.key}-${row.country_code}`}
                            />
                          </td>
                          <td className="px-4 py-3">
                            <Switch
                              checked={!!j.enabled}
                              disabled={savingKey.startsWith(`${job.key}:${row.country_code}:enabled`)}
                              onCheckedChange={(v) => persist(job.key, row.country_code, { enabled: v })}
                              data-testid={`job-toggle-${job.key}-${row.country_code}`}
                            />
                          </td>
                          <td className="px-4 py-3 text-xs">
                            {j.last_run_at ? (
                              <div>
                                <div className="flex items-center gap-1 text-[#0A2540]">
                                  <Calendar size={12} />
                                  <span>{relativeAgo(j.last_run_at)}</span>
                                </div>
                                <div className="text-[10px] text-[#78716C] mt-0.5" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                                  {new Date(j.last_run_at).toLocaleString()}
                                </div>
                              </div>
                            ) : (
                              <span className="text-[#78716C]">Never</span>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            {meta && StatusIcon ? (
                              <div className="flex items-center gap-1.5">
                                <StatusIcon size={14} weight="fill" className={meta.tone} />
                                <span className={`text-xs font-medium ${meta.tone}`}>{meta.label}</span>
                                {typeof j.last_run_entities === 'number' && j.last_run_entities > 0 && (
                                  <span className="text-[10px] text-[#78716C]">· {j.last_run_recipients}/{j.last_run_entities}e</span>
                                )}
                              </div>
                            ) : (
                              <span className="text-xs text-[#78716C]">—</span>
                            )}
                            {j.last_run_error && (
                              <div className="text-[10px] text-[#EF4444] mt-0.5 truncate max-w-xs" title={j.last_run_error}>
                                {j.last_run_error}
                              </div>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
